import type { QueryClient } from '@tanstack/react-query'
import { CartSource } from '@/lib/enums/CartSource'
import type { OrganizationId } from '@/lib/organization-scoping'
import { isForbiddenOrMissingResponse } from '@/lib/route-loader'
import { cartQueries } from '@/lib/services/carts/carts.query'
import type { PosCart } from '@/lib/services/carts/carts.types'

// Create Order reopens the cart this computer last worked on for the shop, so staff who leave the POS mid-order (to look
// up the customer, check deliveries) come back to their order instead of an empty cart. It's kept per computer, not per
// login, because shops share one login across registers.
const LAST_POS_CART_KEY_PREFIX = 'onestem.pos-last-cart'

type ReopenablePosCartFields = Pick<PosCart, 'id' | 'source' | 'sales_order_id' | 'order_change'>

function lastPosCartKey(organizationId: OrganizationId) {
  return `${LAST_POS_CART_KEY_PREFIX}:${organizationId}`
}

export function readLastPosCartId(organizationId: OrganizationId): number | null {
  try {
    const cartId = Number(globalThis.localStorage?.getItem(lastPosCartKey(organizationId)))

    return cartId > 0 ? cartId : null
  } catch {
    return null
  }
}

/** A cart Create Order can reopen: a POS cart that isn't an order yet and isn't a change to one. */
export function canReopenPosCart(
  cart: ReopenablePosCartFields | null | undefined
): cart is ReopenablePosCartFields {
  return cart?.source === CartSource.POS.Value && !cart.sales_order_id && !cart.order_change
}

export function rememberLastPosCart(
  organizationId: OrganizationId,
  cart: ReopenablePosCartFields | null | undefined
) {
  if (!canReopenPosCart(cart)) return

  try {
    globalThis.localStorage?.setItem(lastPosCartKey(organizationId), String(cart.id))
  } catch {
    // Without browser storage, Create Order opens empty, as it did before carts were remembered.
  }
}

/**
 * Leaving a cart on purpose (a new cart, a saved draft, a finished order) stops Create Order reopening it. Only that cart
 * is forgotten, so leaving one in another tab doesn't drop the cart this computer moved on to.
 */
export function forgetLastPosCart(
  organizationId: OrganizationId,
  cartId: number | null | undefined
) {
  if (!cartId || readLastPosCartId(organizationId) !== cartId) return

  try {
    globalThis.localStorage?.removeItem(lastPosCartKey(organizationId))
  } catch {
    // Nothing was stored to forget.
  }
}

// A hover reads the cart moments before the click opens it; the click reuses that read instead of waiting on another.
const REOPEN_READ_FRESH_MS = 10_000

/** The remembered cart, read in the last few seconds so a cart that became an order (on any register) isn't reopened. */
export async function findLastPosCartToReopen(
  queryClient: QueryClient,
  organizationId: OrganizationId
): Promise<number | null> {
  const cartId = readLastPosCartId(organizationId)
  if (!cartId) return null

  let cart: PosCart
  try {
    cart = await queryClient.fetchQuery({
      ...cartQueries.posShow({ organizationId, cartId }),
      staleTime: REOPEN_READ_FRESH_MS,
      // One try, because the click waits on it.
      retry: false,
    })
  } catch (error) {
    // A cart the server says is gone (deleted, another shop's) is let go. A check that failed (a dropped connection, a
    // timeout) opens Create Order empty this once and keeps the cart, so the next visit checks again.
    if (isForbiddenOrMissingResponse(error)) forgetLastPosCart(organizationId, cartId)
    return null
  }

  if (canReopenPosCart(cart)) return cart.id

  forgetLastPosCart(organizationId, cartId)
  return null
}
